import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import { useFormContext, useWatch } from 'react-hook-form'
import { useSelector, useDispatch } from 'react-redux'
import {
  Box,
  Stack,
  Button,
  Grid,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
  Autocomplete,
  TextField,
} from '@mui/material'
import { NoteAlt as NoteIcon, AddCircle as AddCircleIcon, Warning as WarningIcon } from '@mui/icons-material'
import { Controller } from 'react-hook-form'

import store from 'state/store'
import { showFormModal } from 'state/ui'
import { clearCustomer, setCustomer, setCustomers } from 'state/customer'
import { clearTransportationInformation } from 'state/inboundTicket'
import { useGetCustomerQuery } from 'services/customers'
import { useGetPurchaseOrdersV2Query } from 'services/purchaseOrders'

import Input from 'components/generic/form/Input'
import DatePickerV2 from 'components/generic/form/DatePickerV2'
import CustomerAutocomplete from 'components/generic/form/AsyncAutocomplete/CustomerAutocomplete'
import VehicleAutocomplete from 'components/generic/form/VehicleAutocomplete'
import AlertModal from 'components/generic/modals/dialog-modal/AlertModal'
import LicenseAvatar from 'components/containers/inbound-tickets/components/LicenseAvatar'
import CustomerLicenseListItem from './CustomerLicenseListItem'
import CustomerInfoPopover from 'components/generic/popovers/CustomerInfoPopover'
import SkeletonWrapper from 'components/generic/SkeletonWrapper'

import FORM_MODALS from 'utils/ui/modals'
import { isDateInPast, locationIsNotEmpty } from 'utils/helpers'
import { CUSTOMERS_FILTERS } from 'utils/constants'
import { formatVehicle, addToDate } from 'utils/data-manipulation/common'

const CustomerInformation = ({
  disabled,
  inboundTicketLocationId,
  purchaseOrderLocationId,
  onAddVehicle,
  vehicleData,
  isLoading,
  isDirty,
}) => {
  const dispatch = useDispatch()
  const { t } = useTranslation('inboundTicket')

  const methods = useFormContext()
  const {
    control,
    formState: { errors },
    setValue,
    getValues,
  } = methods

  const { customer: supplier } = useSelector(state => state.customer)
  const [locationOptions, setLocationOptions] = useState([])
  const { settings } = useSelector(state => state.settings)
  const canAddCustomer = settings?.purchaseSettings?.inboundTickets?.addCustomersOnInboundTickets
  const { lockAccountingPeriod } = settings?.overviewSettings?.overview || {}
  const { newCustomerVehicle: newCustomerVehicleState } = useSelector(state => state.inboundTicket)
  const [customerVehicle, setCustomerVehicle] = useState(null)
  const customerInput = useWatch({ control, name: 'customer.name' })
  const purchaseOrderInput = useWatch({ control, name: 'transportationInfo.purchaseOrderNumber' })
  const [showRemoveCustomerModal, setShowRemoveCustomerModal] = useState(false)
  const [hasSupplier, setHasSupplier] = useState(false)
  const [contactOptions, setContactOptions] = useState([])
  const { useMinimalObt } = useSelector(state => state.featureFlags?.variables || {})

  const { data: customerData } = useGetCustomerQuery(
    { customerId: customerInput?.id },
    { refetchOnMountOrArgChange: true, skip: !customerInput?.id },
  )

  const { data: purchaseOrdersData, isFetching: isFetchingPOs } = useGetPurchaseOrdersV2Query(
    {
      page: 1,
      pageSize: 1,
      filter: JSON.stringify({
        supplierId: customerData?.id,
        statuses: ['Open'],
      }),
    },
    {
      skip: !customerData?.id,
    },
  )

  const hasPurchaseOrders = purchaseOrdersData?.totalResultsCount > 0

  const validateSupplier = () => {
    if (supplier.customerCommonIdentifierString) return true
    if (customerInput) {
      const { id, customerCommonIdentifierString } = customerInput
      dispatch(setCustomer({ id, customerCommonIdentifierString, supplierType: customerData?.supplierType || '' }))
    }
    return false
  }

  useEffect(() => {
    setHasSupplier(validateSupplier())
  }, [supplier, customerInput])

  const removeCustomer = () => {
    dispatch(clearCustomer())
    setValue('customer.name', '', { shouldDirty: true })
    setLocationOptions([])
    setValue('customer.contactId', '', { shouldDirty: true })
    setValue('customer.locationId', '')
    setValue('customer.vehicle', '', { shouldDirty: true })
    setCustomerVehicle(null)
  }

  const removePurchaseOrder = () => {
    dispatch(clearTransportationInformation())
    setValue('transportationInfo.purchaseOrderNumber', '')
    setValue('transportationInfo.purchaseOrderId', '')
  }

  const handleRemoveCustomer = () => {
    if (purchaseOrderInput) {
      setShowRemoveCustomerModal(true)
    } else {
      removeCustomer()
    }
  }

  useEffect(() => {
    setValue('customer.buysForbidden', customerData?.buysForbidden ? 'No buy' : undefined)
  }, [customerData])

  useEffect(() => {
    if (supplier?.id === customerData?.id) {
      if (!supplier?.supplierType) {
        dispatch(setCustomer({ ...supplier, supplierType: customerData?.supplierType }))
      }
    }
  }, [customerData])

  const [anchorEl, setAnchorEl] = useState(null)
  const handlePopoverOpen = event => {
    setAnchorEl(event.currentTarget)
  }

  const handlePopoverClose = () => {
    setAnchorEl(null)
  }

  useEffect(() => {
    if (supplier?.id === customerData?.id) {
      // Set location options
      const validCustomerLocations = customerData?.locations?.filter(location => locationIsNotEmpty(location)) || []
      setLocationOptions(validCustomerLocations)
      const prioritizedLocationId = isDirty ? purchaseOrderLocationId : inboundTicketLocationId
      if (validCustomerLocations.findIndex(location => location.id === prioritizedLocationId) > -1) {
        setValue('customer.locationId', prioritizedLocationId)
      } else {
        const currentlySelectedLocationId = getValues('customer.locationId')
        if (
          !currentlySelectedLocationId ||
          validCustomerLocations.findIndex(location => location.id === currentlySelectedLocationId) === -1
        ) {
          setValue('customer.locationId', validCustomerLocations[0]?.id || '')
        }
      }

      // Set contact options
      if (customerData?.contacts?.length > 0) {
        setContactOptions(
          customerData.contacts.map(contact => ({
            id: contact.id,
            name: `${contact.firstName || ''} ${contact.lastName || ''}`.trim(),
            email: contact.email,
            phone: contact.phone,
          })),
        )
      } else {
        setContactOptions([])
      }
    }
  }, [customerData, inboundTicketLocationId, purchaseOrderLocationId, supplier?.id])

  useEffect(() => {
    if (vehicleData) {
      setCustomerVehicle(formatVehicle(vehicleData))
    }
  }, [vehicleData])

  useEffect(() => {
    if (customerVehicle?.customerId && customerVehicle.customerId !== customerData?.id) {
      setValue('customer.name', { id: customerVehicle.customerId, customerCommonIdentifierString: '' })
    }
  }, [customerVehicle])

  useEffect(() => {
    if (newCustomerVehicleState?.id) {
      setValue('customer.vehicle', newCustomerVehicleState, { shouldDirty: true })
      setCustomerVehicle(newCustomerVehicleState)
    }
  }, [newCustomerVehicleState?.id])

  // Clear customer-related fields when customer changes
  useEffect(() => {
    if (supplier && supplier.customerCommonIdentifierString) {
      const currentCustomer = getValues('customer.name')
      const hasCustomerChanged = currentCustomer?.id && currentCustomer.id !== supplier.id

      if (hasCustomerChanged) {
        setValue('customer.contactId', '', { shouldDirty: true })
        setValue('customer.locationId', '', { shouldDirty: true })
        setValue('customer.vehicle', null, { shouldDirty: true })
        setCustomerVehicle(null)
      }
    }
  }, [supplier])

  // Watch vehicle field and clear local state when it's cleared
  const vehicleField = useWatch({ control, name: 'customer.vehicle' })
  useEffect(() => {
    if (vehicleField === null || vehicleField === '') {
      setCustomerVehicle(null)
    }
  }, [vehicleField])

  const renderSupplier = () => {
    if (isLoading) {
      return (
        <Grid item xs={12}>
          <SkeletonWrapper isLoading={isLoading}>
            <CustomerAutocomplete
              name="customer.name"
              label={t('supplier_name')}
              data-test="customer-info-name"
              variant="outlined"
              size="small"
              control={control}
              error={!!errors.customer?.name}
              helperText={errors.customer?.name?.message}
              onCustomersLoad={data => dispatch(setCustomers(data?.Items || []))}
              freeSolo={false}
              baseFilter={CUSTOMERS_FILTERS.SUPPLIER}
              autocompleteProps={{
                renderOption: (props, option) => (
                  <CustomerLicenseListItem props={props} option={option} key={option.id} />
                ),
                filterOptions: options => options,
              }}
            />
          </SkeletonWrapper>
        </Grid>
      )
    }
    if (hasSupplier) {
      return (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          borderRadius={1}
          minHeight={40}
          border="1px solid"
          borderColor="grey.400"
          onClick={handlePopoverOpen}
          sx={{
            cursor: 'pointer',
            p: 1,
            '&:hover': {
              backgroundColor: 'primary.lightBg',
            },
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={1}
            sx={{
              flexGrow: 1,
              overflow: 'hidden',
              flexWrap: { xs: 'wrap', md: 'nowrap' },
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
              <LicenseAvatar selectedCustomer={customerInput || null} customerData={customerData} />
              <Typography sx={{ fontWeight: 'medium' }}>{supplier?.customerCommonIdentifierString || ''}</Typography>

              {customerData?.notes && (
                <Tooltip title={customerData?.notes}>
                  <NoteIcon color="info" fontSize="small" />
                </Tooltip>
              )}
            </Stack>

            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 1,
                mt: { xs: 1, sm: 0 },
              }}
            >
              {!purchaseOrderInput &&
                (isFetchingPOs ? (
                  <CircularProgress size={16} />
                ) : (
                  <Typography
                    variant="caption"
                    color={hasPurchaseOrders ? 'success.main' : 'text.secondary'}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      px: 1,
                      py: 0.5,
                    }}
                  >
                    {hasPurchaseOrders
                      ? t('purchase_orders_available_count', { count: purchaseOrdersData?.totalResultsCount })
                      : t('no_purchase_orders')}
                  </Typography>
                ))}

              {isDateInPast(customerData?.contacts?.[0]?.licenseExpiration) && (
                <Typography
                  variant="caption"
                  color="error.main"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    bgcolor: 'error.lighter',
                  }}
                >
                  <WarningIcon color="error" fontSize="small" sx={{ mr: 0.5 }} />
                  {t('license_expired')}
                </Typography>
              )}

              {customerData?.buysForbidden && (
                <Typography
                  variant="caption"
                  color="warning.main"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    bgcolor: 'warning.lighter',
                  }}
                >
                  <WarningIcon color="warning" fontSize="small" sx={{ mr: 0.5 }} />
                  {t('no_buy')}
                </Typography>
              )}
            </Box>
          </Stack>

          <Button
            onClick={e => {
              e.stopPropagation()
              handleRemoveCustomer()
            }}
            disabled={disabled}
            size="small"
            variant="outlined"
            color="error"
            sx={{
              flexShrink: 0,
              minWidth: 'auto',
              px: 1,
              py: 0.5,
              fontSize: '0.75rem',
              lineHeight: 1,
              textTransform: 'none',
            }}
          >
            {t('remove')}
          </Button>
        </Stack>
      )
    }
    return (
      <Stack direction="row" spacing={2}>
        <Box sx={{ flex: 3 }}>
          <CustomerAutocomplete
            name="customer.name"
            label={t('supplier_name')}
            data-test="customer-info-name"
            variant="outlined"
            size="small"
            control={control}
            error={!!errors.customer?.name}
            helperText={errors.customer?.name?.message}
            onCustomersLoad={data => dispatch(setCustomers(data?.Items || []))}
            freeSolo={false}
            baseFilter={CUSTOMERS_FILTERS.SUPPLIER}
            autocompleteProps={{
              renderOption: (props, option) => (
                <CustomerLicenseListItem props={props} option={option} key={option.id} />
              ),
              filterOptions: options => options,
            }}
          />
        </Box>
        {canAddCustomer && (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
            <Button
              fullWidth
              variant="text"
              startIcon={<AddCircleIcon />}
              onClick={() =>
                store.dispatch(
                  showFormModal({
                    formModalName: FORM_MODALS.NEW_CUSTOMER,
                    formModalProps: { type: 'supplier' },
                  }),
                )
              }
            >
              {t('add')}
            </Button>
          </Box>
        )}
      </Stack>
    )
  }

  return (
    <>
      <CustomerInfoPopover selectedCustomer={customerData} anchorEl={anchorEl} onClose={handlePopoverClose} />
      <AlertModal
        open={showRemoveCustomerModal}
        modalType="remove"
        title={t('remove_customer_title')}
        question={t('remove_customer_question')}
        description={t('remove_customer_description')}
        confirmLabel={t('confirm_remove_customer')}
        cancelLabel={t('cancel_action')}
        onClose={() => {
          setShowRemoveCustomerModal(false)
        }}
        onSubmit={() => {
          removeCustomer()
          removePurchaseOrder()
          setShowRemoveCustomerModal(false)
        }}
      />
      <Box width={{ xs: '100%', md: '50%' }} data-testid="customer-info">
        <Typography variant="h6" gutterBottom color="textSecondary">
          {t('customer_information')}
        </Typography>
        <Stack
          padding={2}
          spacing={2}
          sx={{
            backgroundColor: 'background.section',
            borderRadius: '8px',
          }}
        >
          {/* Supplier Section */}
          <Stack direction="row" spacing={2}>
            <Box flex={1}>{renderSupplier()}</Box>
          </Stack>

          {/* Effective Date, Contact, and Location Section */}
          <Stack direction="row" spacing={2}>
            <Box flex={1}>
              <SkeletonWrapper isLoading={isLoading}>
                <DatePickerV2
                  name="effectiveDate"
                  label={t('effective_date')}
                  data-test="customer-info-effectivedate"
                  variant="outlined"
                  size="small"
                  datePickerProps={{
                    disabled,
                    minDate: lockAccountingPeriod.isLockedForAccounting
                      ? addToDate(new Date(lockAccountingPeriod.lockAccountingDate))
                      : undefined,
                  }}
                  error={!!errors.effectiveDate}
                  helperText={errors.effectiveDate?.message}
                  control={control}
                  defaultDate={new Date()}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </SkeletonWrapper>
            </Box>
            {!useMinimalObt && (
              <Box flex={1}>
                <SkeletonWrapper isLoading={isLoading}>
                  <Controller
                    name="customer.contactId"
                    control={control}
                    defaultValue=""
                    render={({ field: { onChange, value } }) => (
                      <Autocomplete
                        options={contactOptions}
                        getOptionLabel={option => option.name || ''}
                        value={contactOptions.find(opt => opt.id === value) || null}
                        onChange={(_, newValue) => onChange(newValue?.id || '')}
                        disabled={disabled || !hasSupplier || contactOptions.length === 0}
                        renderInput={params => (
                          <TextField
                            {...params}
                            label={t('contact')}
                            variant="outlined"
                            size="small"
                            error={!!errors.customer?.contactId}
                            helperText={errors.customer?.contactId?.message}
                            data-test="customer-contact-id"
                          />
                        )}
                        renderOption={(props, option) => (
                          <li {...props}>
                            <Stack direction="column" spacing={0.5}>
                              <Typography variant="body2">{option.name}</Typography>
                              {option.email && (
                                <Typography variant="caption" color="text.secondary">
                                  {option.email}
                                </Typography>
                              )}
                              {option.phone && (
                                <Typography variant="caption" color="text.secondary">
                                  {option.phone}
                                </Typography>
                              )}
                            </Stack>
                          </li>
                        )}
                      />
                    )}
                  />
                </SkeletonWrapper>
              </Box>
            )}
            <Box flex={1}>
              <SkeletonWrapper isLoading={isLoading}>
                <Controller
                  name="customer.locationId"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <Autocomplete
                      options={locationOptions}
                      getOptionLabel={option => option.name || ''}
                      value={locationOptions.find(opt => opt.id === value) || null}
                      onChange={(_, newValue) => onChange(newValue?.id || '')}
                      disabled={disabled || !hasSupplier || locationOptions.length === 0}
                      renderInput={params => (
                        <TextField
                          {...params}
                          label={t('location')}
                          variant="outlined"
                          size="small"
                          error={!!errors.customer?.locationId}
                          helperText={errors.customer?.locationId?.message}
                          data-test="customer-location-id"
                        />
                      )}
                      disableClearable
                    />
                  )}
                />
              </SkeletonWrapper>
            </Box>
          </Stack>

          {/* Number of Passengers and Vehicle Section */}
          {!useMinimalObt && (
            <Stack direction="row" spacing={2} alignItems="center">
              <Box flex={1}>
                <SkeletonWrapper isLoading={isLoading}>
                  <Input
                    name="numberOfPassengers"
                    label={t('number_of_passengers')}
                    data-test="customer-info-no-passengers"
                    variant="outlined"
                    size="small"
                    defaultValue=""
                    disabled={disabled}
                    control={control}
                    error={!!errors.numberOfPassengers}
                    helperText={errors.numberOfPassengers?.message}
                  />
                </SkeletonWrapper>
              </Box>
              <Box flex={1} sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ flex: 1 }}>
                  <SkeletonWrapper isLoading={isLoading}>
                    <VehicleAutocomplete
                      name="customer.vehicle"
                      control={control}
                      data-test="customer-vehicle"
                      size="small"
                      customerVehicle={customerVehicle}
                      setCustomerVehicle={setCustomerVehicle}
                      setValue={setValue}
                      disabled={disabled}
                      hasSupplier={hasSupplier}
                      customerVehicleOptions={
                        customerInput?.id &&
                        customerData?.id === customerInput?.id &&
                        customerData?.vehicles?.length > 0
                          ? customerData?.vehicles
                          : []
                      }
                      customerId={customerInput?.id}
                    />
                  </SkeletonWrapper>
                </Box>
                <IconButton
                  id="add-customer-vehicle-button"
                  onClick={() => onAddVehicle('customer-vehicle')}
                  disabled={disabled}
                  sx={{ ml: 1 }}
                >
                  <AddCircleIcon />
                </IconButton>
              </Box>
            </Stack>
          )}
        </Stack>
      </Box>
    </>
  )
}

CustomerInformation.propTypes = {
  disabled: PropTypes.bool,
  inboundTicketLocationId: PropTypes.string,
  purchaseOrderLocationId: PropTypes.string,
  onAddVehicle: PropTypes.func.isRequired,
  vehicleData: PropTypes.object,
  isLoading: PropTypes.bool,
  isDirty: PropTypes.bool,
}

CustomerInformation.defaultProps = {
  disabled: false,
  inboundTicketLocationId: null,
  purchaseOrderLocationId: null,
  vehicleData: null,
  isLoading: false,
  isDirty: false,
}

export default CustomerInformation
